import ExcelJS from 'exceljs'
import { useEffect, useState } from 'react'
import { Cluster, Grade, Module, Strand } from 'src/types/type'

// Define the shape of the transformation functions
type TransformFunction<T> = (rawData: string[][]) => T[]
const modulesDataSheetIndex = 0
const gradesDataSheetIndex = 1
const correlationsDataSheetIndex = 2
const gradeDataSliceValue = 3
const columnIndex = 5

const transformData: TransformFunction<Grade> = (rawData) => {
  const data: Grade[] = []

  rawData.slice(gradesDataSheetIndex).forEach((row) => {
    const [grade, clusterName, strandName, fimModules] = row as [
      string,
      string,
      string,
      string
    ]
    let gradeObj = data.find((g) => g.grade === grade)
    if (!gradeObj) {
      gradeObj = { grade, clusters: [] }
      data.push(gradeObj)
    }

    let clusterObj = gradeObj.clusters.find((c) => c.name === clusterName)
    if (!clusterObj) {
      clusterObj = { name: String(clusterName), strands: [] }
      gradeObj.clusters.push(clusterObj)
    }

    const fimModulesValue: string | string[] =
      fimModules !== undefined ? String(fimModules) : ''

    clusterObj.strands.push({
      name: String(strandName),
      fimModules: fimModulesValue
    })
  })
  return data
}

const transformModuleData: TransformFunction<Module> = (rawData) => {
  const data: Module[] = []

  rawData.slice(gradesDataSheetIndex).forEach((row) => {
    const [module, gameName, category] = row as [string, string, string]

    if (!module) return

    let moduleObj = data.find((d) => d.module === module)
    if (!moduleObj) {
      moduleObj = { module: String(module), games: [] }
      data.push(moduleObj)
    }
    let gameObj = moduleObj.games.find((g) => g.name === gameName)
    if (!gameObj) {
      gameObj = { name: gameName, category: [] }
      if (gameObj.name !== null) {
        moduleObj.games.push(gameObj)
      }
    }
    const categoryObj = { name: category }
    if (categoryObj.name !== null && categoryObj.name !== undefined) {
      const categoryExists = gameObj.category.some(
        (cat) => cat.name === categoryObj.name
      )
      if (!categoryExists) {
        gameObj.category.push(categoryObj)
      }
    }
  })
  return data
}

const transformCorrelationData: TransformFunction<Grade> = (rawData) => {
  const data: Grade[] = []

  rawData.slice(gradesDataSheetIndex).forEach((row) => {
    const [grade, clusterName, strand, code, strandName, fimModules] = row as [
      string,
      string,
      string,
      string,
      string,
      string
    ]

    let gradeObj = data.find((g) => g.grade === grade)
    if (!gradeObj) {
      gradeObj = { grade, clusters: [] }
      data.push(gradeObj)
    }

    let clusterObj = gradeObj.clusters.find((c) => c.name === clusterName)
    if (!clusterObj) {
      clusterObj = { name: String(clusterName), strands: [] }
      gradeObj.clusters.push(clusterObj)
    }

    const fimModulesValue: string | string[] =
      fimModules !== undefined ? String(fimModules) : ''
    const strandValue: string | string[] =
      strand !== undefined ? String(strand) : ''

    clusterObj.strands.push({
      strand: strandValue,
      code: String(code),
      name: String(strandName),
      fimModules: fimModulesValue
    })
  })
  return data
}
const defaultGrade = 'K'
export function useDataLoader() {
  const [selectedCluster, setSelectedCluster] = useState<Cluster>({
    name: '',
    strands: []
  })
  const [grade, setGrade] = useState<string | string[]>(defaultGrade) // Grade state
  const [strands, setStrands] = useState<Strand[]>([])
  const [gradeData, setGradeData] = useState<Grade[]>([])
  const [selectedModule, setSelectedModule] = useState<Module>({
    module: '',
    games: []
  })
  const [moduleData, setModuleData] = useState<Module[]>([])
  const [selectedCorrelation, setSelectedCorrelation] = useState<Cluster>({
    name: '',
    strands: []
  })
  const [correlationData, setCorrelationData] = useState<Grade[]>([])

  // Use effect hook to dynamically fetch process, and display Excel-based data
  useEffect(() => {
    // Function to fetch and transform data from an Excel file
    const fetchAndTransformData = async <T>(
      url: string,
      sheetIndex: number,
      transform: (data: string[][]) => T[],
      setData: (data: T[]) => void
    ) => {
      try {
        // Fetch the Excel file as a binary ArrayBuffer
        const response: Response = await fetch(url)
        const arrayBuffer: ArrayBuffer = await response.arrayBuffer()
        // Load the Excel workbook using ExcelJS
        const workbook: ExcelJS.Workbook = new ExcelJS.Workbook()
        await workbook.xlsx.load(arrayBuffer)
        // Access the specified worksheet by index
        const worksheet: ExcelJS.Worksheet = workbook.worksheets[sheetIndex]
        const jsonData: string[][] = []
        // Iterate through all rows in the worksheet
        worksheet.eachRow({ includeEmpty: true }, (row) => {
          // Extract row values and convert them into a string array
          const rowData = row.values as string[]
          if (rowData[modulesDataSheetIndex] === undefined) {
            rowData.shift() // Remove the first undefined value if present
          }
          // Slice the row data for specific sheet types
          let sliceValue = rowData.length
          if (sheetIndex === gradesDataSheetIndex)
            sliceValue = gradeDataSliceValue
          const finalRowData = Array.isArray(rowData)
            ? rowData.slice(modulesDataSheetIndex, sliceValue)
            : []
          // Add specific column data for grades sheet
          if (sheetIndex === gradesDataSheetIndex && Array.isArray(rowData))
            finalRowData.push(rowData[columnIndex])
          // Collect the processed row data
          jsonData.push(finalRowData)
        })
        // Apply the provided transformation function to format the data
        const formattedData = transform(jsonData)
        // Update the corresponding state with the transformed data
        setData(formattedData)
        // Return the transformed data for further use
        return formattedData
      } catch (error) {
        console.error('Error fetching or parsing the Excel file:', error)
      }
      return []
    }

    // Function to initialize the application data
    const initializeData = async () => {
      // Fetch, transform, and initialize grade data
      const formattedGradeData = await fetchAndTransformData(
        '/ccsm.xlsx',
        gradesDataSheetIndex,
        transformData,
        setGradeData
      )
      if (
        formattedGradeData &&
        formattedGradeData.length > modulesDataSheetIndex
      ) {
        // Initialize related UI states based on grade data
        setSelectedCluster(
          formattedGradeData[modulesDataSheetIndex]?.clusters[
            modulesDataSheetIndex
          ]
        )
        setGrade(String(formattedGradeData[modulesDataSheetIndex].grade))
        setStrands(
          formattedGradeData[modulesDataSheetIndex].clusters[
            modulesDataSheetIndex
          ].strands
        )
      }
      // Fetch, transform, and initialize module data
      const formattedModuleData = await fetchAndTransformData(
        '/hub-linear.xlsx',
        modulesDataSheetIndex,
        transformModuleData,
        setModuleData
      )
      if (
        formattedModuleData &&
        formattedModuleData.length > modulesDataSheetIndex
      ) {
        // Initialize selected module based on the data
        setSelectedModule(formattedModuleData[modulesDataSheetIndex])
      }

      // Fetch, transform, and initialize correlation data
      const formattedCorrelationData = await fetchAndTransformData(
        '/ccsm.xlsx',
        correlationsDataSheetIndex,
        transformCorrelationData,
        setCorrelationData
      )
      if (
        formattedCorrelationData &&
        formattedCorrelationData.length > modulesDataSheetIndex
      ) {
        // Initialize selected correlation cluster
        setSelectedCorrelation(
          formattedCorrelationData[modulesDataSheetIndex].clusters[
            modulesDataSheetIndex
          ]
        )
      }
    }

    // Call the initialization function to load and set all required data
    initializeData()
  }, [])

  return {
    selectedCluster,
    grade,
    strands,
    gradeData,
    selectedModule,
    moduleData,
    selectedCorrelation,
    correlationData,
    setSelectedCluster,
    setGrade,
    setStrands,
    setSelectedModule,
    setSelectedCorrelation,
    setCorrelationData,
    defaultGrade,
    setGradeData
  }
}
